import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Returns a trigger that invalidates `keys` at most once per `ms` (trailing edge: the first call
 * schedules one invalidation; calls while it is pending are coalesced). Safe to call from realtime handlers.
 */
export function useThrottledInvalidate(keys: QueryKey[], ms: number): () => void {
  const qc = useQueryClient();
  const keysJson = JSON.stringify(keys);
  const stableKeys = useMemo(() => JSON.parse(keysJson) as QueryKey[], [keysJson]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    },
    [],
  );
  return useCallback(() => {
    if (timer.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      for (const queryKey of stableKeys) void qc.invalidateQueries({ queryKey });
    }, ms);
  }, [qc, stableKeys, ms]);
}
